import React, { useCallback, useEffect, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, List, ChevronLeft, AlertTriangle } from 'lucide-react';
import { EpisodeList } from './EpisodeList';
interface EpisodeNavScreenProps {
  onPlay: () => void;
  onBack: () => void;
  onNextEpisode: () => void;
}
export function EpisodeNavScreen({
  onPlay,
  onBack,
  onNextEpisode
}: EpisodeNavScreenProps) {
  const [isListOpen, setIsListOpen] = useState(false);
  const [hasWatched, setHasWatched] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const handlePlayFullscreen = useCallback(async () => {
    const video = videoRef.current;
    const container = videoContainerRef.current;
    if (!video || !container) return;
    setIsPlaying(true);
    try {
      if (container.requestFullscreen) {
        await container.requestFullscreen();
      } else if ((container as any).webkitRequestFullscreen) {
        await (container as any).webkitRequestFullscreen();
      }
      await video.play();
    } catch (err) {
      await video.play();
    }
  }, []);
  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if ((document as any).webkitFullscreenElement) {
      ;
      (document as any).webkitExitFullscreen();
    }
  }, []);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleEnded = () => {
      setIsPlaying(false);
      setHasWatched(true);
      exitFullscreen();
    };
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && !(document as any).webkitFullscreenElement) {
        if (video && !video.ended && !video.paused) {
          // Video still playing inline
        }
        if (video && video.ended) {
          setIsPlaying(false);
          setHasWatched(true);
        }
      }
    };
    video.addEventListener('ended', handleEnded);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      video.removeEventListener('ended', handleEnded);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, [exitFullscreen]);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    },
    exit: {
      opacity: 0,
      y: -50,
      transition: {
        duration: 0.5
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };
  return <motion.div className="min-h-screen w-full bg-[var(--bg-secondary)] flex flex-col relative overflow-hidden" variants={containerVariants} initial="hidden" animate="visible" exit="exit" data-id="element-341">
      <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] pointer-events-none mix-blend-overlay" data-id="element-342" />

      <div ref={videoContainerRef} className={`${isPlaying && !hasWatched ? 'block' : 'hidden'} fixed inset-0 z-50 bg-black flex items-center justify-center`} data-id="element-343">
        <video ref={videoRef} className="w-full h-full object-contain bg-black" controls controlsList="nodownload noplaybackrate" disablePictureInPicture onContextMenu={e => e.preventDefault()} playsInline preload="auto" poster="https://images.unsplash.com/photo-1478720568477-152d9b164e63?q=80&w=2500&auto=format&fit=crop" data-id="element-344">
          <source src="https://cdn.shopify.com/videos/c/o/v/46f24469e7894a26a2d058257b630f2c.mp4" type="video/mp4" data-id="element-345" />
        </video>
      </div>

      <nav className="w-full p-6 flex justify-between items-center z-30" data-id="element-346">
        <button onClick={onBack} className="flex items-center text-[var(--text-secondary)] hover:text-white transition-colors text-sm tracking-widest uppercase group" data-id="element-347">
          <ChevronLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-348" />
          Volver
        </button>
      </nav>

      <div className="flex-1 flex flex-col justify-center items-center px-6 md:px-12 z-20" data-id="element-349">
        <div className="max-w-4xl w-full mx-auto" data-id="element-350">
          <motion.div variants={itemVariants} className="mb-4" data-id="element-351">
            <span className="text-[var(--text-secondary)] uppercase tracking-[0.2em] text-xs md:text-sm font-medium border-l-2 border-[var(--accent)] pl-4" data-id="element-352">
              Temporada 1 — Caso del Auto Blanco
            </span>
          </motion.div>

          <motion.h2 variants={itemVariants} className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-[var(--text-primary)] mb-8 leading-tight" data-id="element-353">
            EPISODIO 1 — <br className="md:hidden" data-id="element-354" />
            <span className="text-white" data-id="element-355">DESAPARECIDA</span>
          </motion.h2>

          <motion.div variants={itemVariants} className="w-24 h-1 bg-[var(--accent)] mb-10" data-id="element-356" />

          <motion.div variants={itemVariants} className="space-y-4 mb-12 max-w-2xl" data-id="element-357">
            <p className="text-lg md:text-xl font-light text-[var(--text-primary)] leading-relaxed" data-id="element-358">
              Carmen García desaparece.
            </p>
            <p className="text-lg md:text-xl font-light text-[var(--text-primary)] leading-relaxed" data-id="element-359">
              Cinco días después, su cuerpo aparece en Guadarrama.
            </p>
            <p className="text-lg md:text-xl font-light text-[var(--text-primary)] leading-relaxed" data-id="element-360">
              Tres personas están en el centro de la historia.
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-start sm:items-center gap-6" data-id="element-361">
            <button onClick={handlePlayFullscreen} className="flex items-center justify-center px-8 py-4 bg-[var(--accent)] text-white font-medium tracking-widest uppercase hover:bg-[var(--accent-hover)] transition-all duration-300 shadow-[0_0_30px_rgba(139,26,26,0.3)] hover:shadow-[0_0_50px_rgba(139,26,26,0.5)] hover:scale-105" data-id="element-362">
              <Play className="w-5 h-5 mr-3 fill-current" data-id="element-363" />
              {hasWatched ? 'Volver a Ver' : 'Ver Episodio 1'}
            </button>

            <button onClick={() => setIsListOpen(true)} className="flex items-center text-[var(--text-primary)] hover:text-[var(--accent)] transition-colors text-sm tracking-widest uppercase group py-2" data-id="element-364">
              <List className="w-5 h-5 mr-3" data-id="element-365" />
              <span className="group-hover:underline underline-offset-4 decoration-1" data-id="element-366">
                Ver lista de episodios
              </span>
            </button>
          </motion.div>

          <AnimatePresence data-id="element-367">
            {hasWatched && <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="mt-16 space-y-12 max-w-3xl" data-id="element-368">
                <div className="w-full h-px bg-white/10" data-id="element-369" />

                <div className="space-y-6" data-id="element-370">
                  <h3 className="text-xl font-serif text-[var(--text-primary)] mb-4" data-id="element-371">
                    En este episodio conocerás:
                  </h3>
                  <ul className="space-y-3 text-[var(--text-secondary)] font-light" data-id="element-372">
                    <li className="flex items-center" data-id="element-373">
                      <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3" data-id="element-374" />
                      La última noche de Carmen
                    </li>
                    <li className="flex items-center" data-id="element-375">
                      <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3" data-id="element-376" />
                      La llamada misteriosa
                    </li>
                    <li className="flex items-center" data-id="element-377">
                      <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3" data-id="element-378" />
                      Los tres principales sospechosos
                    </li>
                  </ul>
                </div>

                <motion.div className="bg-[var(--bg-card)] border-l-4 border-[var(--accent)] p-6 md:p-8 relative overflow-hidden" initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3
            }} data-id="element-379">
                  <div className="absolute top-0 right-0 p-4 opacity-5" data-id="element-380">
                    <AlertTriangle className="w-24 h-24" data-id="element-381" />
                  </div>
                  <div className="relative z-10" data-id="element-382">
                    <div className="flex items-center mb-4 text-[var(--accent)]" data-id="element-383">
                      <AlertTriangle className="w-5 h-5 mr-2" data-id="element-384" />
                      <span className="font-bold tracking-widest uppercase text-sm" data-id="element-385">
                        Importante
                      </span>
                    </div>
                    <p className="text-[var(--text-primary)] text-lg mb-2" data-id="element-386">
                      Este episodio incluye una pausa obligatoria para que
                      investiguen.
                    </p>
                    <p className="text-[var(--text-secondary)]" data-id="element-387">
                      No usen el celular.
                    </p>
                  </div>
                </motion.div>

                <motion.div className="flex flex-col items-center pt-4 pb-12 gap-6" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.6
            }} data-id="element-388">
                  <button onClick={onNextEpisode} className="px-8 py-4 border border-white/10 text-[var(--text-secondary)] uppercase tracking-widest text-sm hover:bg-white/5 hover:text-white hover:border-white/30 transition-all duration-300" data-id="element-389">
                    Continuar al Episodio 2
                  </button>
                </motion.div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      <EpisodeList isOpen={isListOpen} onClose={() => setIsListOpen(false)} onSelectEpisode={id => {
      setIsListOpen(false);
      if (id === 1) handlePlayFullscreen();
    }} data-id="element-390" />
    </motion.div>;
}